<div #scrollContainer class="financial-impact-report">
  <div class="financial-impact-report__close-header">
    <mat-icon class="financial-impact-report__close-icon" (click)="onClose()">close</mat-icon>
  </div>

  <h1 class="financial-impact-report__title" [style.color]="wls.accentColor">
    @if (step() === 'cart') {
      {{ 'employee.cart-simulation.title' | translate }}
    } @else {
      {{ 'employee.financial-impact-report.title' | translate }}
    }
  </h1>

  @if (isLoading()) {
    <div class="financial-impact-report__state">
      <flexyoo-loader></flexyoo-loader>
    </div>
  } @else if (hasError()) {
    <div class="financial-impact-report__state">
      <p>{{ 'employee.financial-impact-report.load-error' | translate }}</p>
      <button matButton="outlined" type="button" (click)="onRetry()">
        {{ 'employee.financial-impact-report.retry' | translate }}
      </button>
    </div>
  } @else if (isCartEmpty()) {
    <div class="financial-impact-report__state">
      <p>{{ 'employee.financial-impact-report.empty-cart' | translate }}</p>
    </div>
  } @else if (step() === 'cart') {
    @if (cartSimulation(); as cartSimulation) {
      <flexyoo-employee-cart-simulation [view]="cartSimulation" />
    }
  } @else if (report(); as report) {
    <!-- 1. Budget types -->
    <section class="financial-impact-report__section">
      <h2 class="financial-impact-report__section-title">
        {{ 'employee.financial-impact-report.budget-types' | translate }}
      </h2>

      @if (report.fundingSources.length) {
        <!-- 51392: as on the PDF, Primes & Bonuses come first, under the gross amount spent -->
        <div class="financial-impact-report__rows">
          <div class="financial-impact-report__row financial-impact-report__row_highlighted">
            <span>
              {{ 'employee.financial-impact-report.primes-and-bonuses-total' | translate }}
            </span>
            <span class="financial-impact-report__amount">
              {{ report.fundingSourcesSpentGrossAmount | flexyooNumber }}
            </span>
          </div>
        </div>

        <!-- column labels are shared with the employee budget page -->
        <div class="financial-impact-report__table-scroll">
          <table class="financial-impact-report__budget-table">
            <thead>
              <tr>
                <th>{{ 'employee.budget.type' | translate }}</th>
                <th>{{ 'employee.budget.assignedGrossAmount' | translate }}</th>
                <th>{{ 'employee.budget.assignedBudgetAmount' | translate }}</th>
                <th>{{ 'employee.budget.spentBudgetAmount' | translate }}</th>
                <th>{{ 'employee.budget.remainingBudgetAmount' | translate }}</th>
                <th>{{ 'employee.budget.remainingGrossAmount' | translate }}</th>
              </tr>
            </thead>
            <tbody>
              @for (source of report.fundingSources; track source.translationKey) {
                <tr>
                  <td>{{ 'funding.report-title.' + source.translationKey | translate }}</td>
                  <td class="financial-impact-report__amount">
                    {{ source.assignedGrossAmount | flexyooNumber }}
                  </td>
                  <td class="financial-impact-report__amount">
                    {{ source.assignedBudgetAmount | flexyooNumber }}
                  </td>
                  <td class="financial-impact-report__amount">
                    {{ source.spentBudgetAmount | flexyooNumber }}
                  </td>
                  <td class="financial-impact-report__amount">
                    {{ source.remainingBudgetAmount | flexyooNumber }}
                  </td>
                  <td class="financial-impact-report__amount">
                    {{ source.remainingGrossAmount | flexyooNumber }}
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      }

      @if (report.salarySacrifice; as salarySacrifice) {
        <!-- rows in the order of the PDF of the report -->
        <div class="financial-impact-report__rows">
          <div class="financial-impact-report__row financial-impact-report__row_highlighted">
            <span>
              {{ 'employee.financial-impact-report.salary-sacrifice-total' | translate }}
            </span>
            <span class="financial-impact-report__amount">
              {{ salarySacrifice.totalMonthlySacrificeAmount | flexyooNumber }}
            </span>
          </div>
          <div class="financial-impact-report__row">
            <span>
              {{ 'employee.financial-impact-report.initial-monthly-gross-salary' | translate }}
            </span>
            <span class="financial-impact-report__amount">
              {{ salarySacrifice.initialMonthlyGrossSalary | flexyooNumber }}
            </span>
          </div>
          @if (salarySacrifice.existingMonthlySacrificeAmount) {
            <div class="financial-impact-report__row">
              <span>
                {{
                  'employee.financial-impact-report.monthly-salary-sacrifice-existing' | translate
                }}
              </span>
              <span class="financial-impact-report__amount">
                {{ salarySacrifice.existingMonthlySacrificeAmount | flexyooNumber }}
              </span>
            </div>
          }
          <div class="financial-impact-report__row financial-impact-report__row_current">
            <span>
              {{ 'employee.financial-impact-report.monthly-salary-sacrifice' | translate }}
            </span>
            <span class="financial-impact-report__amount">
              {{ salarySacrifice.monthlySacrificeAmount | flexyooNumber }}
            </span>
          </div>
          <div class="financial-impact-report__row">
            <span>
              {{ 'employee.financial-impact-report.new-monthly-gross-salary' | translate }}
            </span>
            <span class="financial-impact-report__amount">
              {{ salarySacrifice.newMonthlyGrossSalary | flexyooNumber }}
            </span>
          </div>
        </div>
      }
    </section>

    <!-- 2. Selected benefits -->
    <section class="financial-impact-report__section">
      <h2 class="financial-impact-report__section-title">
        {{ 'employee.financial-impact-report.selected-benefits' | translate }}
      </h2>

      <!-- 50477: as on the PDF, the paid long-term benefits get a table of their own -->
      @if (report.longTermBenefits.length) {
        <h3 class="financial-impact-report__subsection-title">
          {{ 'employee.financial-impact-report.long-term-benefits' | translate }}
        </h3>
        <ng-container
          [ngTemplateOutlet]="benefitsTable"
          [ngTemplateOutletContext]="{
            benefits: report.longTermBenefits,
          }"
        />
        @if (report.benefits.length) {
          <h3 class="financial-impact-report__subsection-title">
            {{ 'employee.financial-impact-report.new-benefits' | translate }}
          </h3>
        }
      }
      @if (report.benefits.length || !report.longTermBenefits.length) {
        <ng-container
          [ngTemplateOutlet]="benefitsTable"
          [ngTemplateOutletContext]="{
            benefits: report.benefits,
          }"
        />
      }
    </section>

    <!-- 3. Financial impact -->
    <section class="financial-impact-report__section">
      <div class="financial-impact-report__comparison-header">
        <h2 class="financial-impact-report__section-title">
          {{ 'employee.financial-impact-report.financial-impact' | translate }}
        </h2>
        <span class="financial-impact-report__column-label">
          {{ 'employee.financial-impact-report.before-selection' | translate }}
        </span>
        <span class="financial-impact-report__column-label">
          {{ 'employee.financial-impact-report.after-selection' | translate }}
        </span>
      </div>

      <div class="financial-impact-report__card">
        <div class="financial-impact-report__comparison">
          @for (row of report.comparisonRows; track row.key) {
            <div
              class="financial-impact-report__comparison-row"
              [class.financial-impact-report__comparison-row_highlighted]="row.isHighlighted"
            >
              <span>
                @if (row.isSubtracted) {
                  {{ 'employee.financial-impact-report.minus-prefix' | translate }}
                }
                {{ row.labelKey | translate }}
              </span>
              <span class="financial-impact-report__amount">
                {{ row.before | flexyooNumber }}
              </span>
              <span class="financial-impact-report__amount">
                {{ row.after | flexyooNumber }}
              </span>
            </div>
          }
        </div>

        <div class="financial-impact-report__total">
          <span>{{ 'employee.financial-impact-report.annual-package-impact' | translate }}</span>
          <span
            class="financial-impact-report__total-amount financial-impact-report__total-amount_end"
          >
            {{ report.annualNetPackageImpact | flexyooNumber }}
          </span>
        </div>
      </div>
    </section>

    <!-- Benefits funded through salary package conversion -->
    <section class="financial-impact-report__section">
      <div class="financial-impact-report__card">
        <div class="financial-impact-report__package-title">
          {{ 'employee.financial-impact-report.package.title' | translate }}
        </div>
        <div
          class="financial-impact-report__package-row financial-impact-report__package-row_header"
        >
          <span>{{ 'employee.financial-impact-report.package.benefits' | translate }}</span>
          <span class="financial-impact-report__total-amount">
            {{ 'employee.financial-impact-report.package.value' | translate }}
          </span>
        </div>

        <div class="financial-impact-report__package-rows">
          @for (row of report.packageRows; track row.benefitId) {
            <div class="financial-impact-report__package-row">
              <span>{{ row.category | flexyooLocalizedName: currLang() }}</span>
              <span class="financial-impact-report__total-amount">
                @if (row.value > 0) {
                  {{ row.value | flexyooNumber }}
                } @else {
                  —
                }
              </span>
            </div>
          }
        </div>

        <div class="financial-impact-report__total">
          <span>{{ 'employee.financial-impact-report.total-package-impact' | translate }}</span>
          <span class="financial-impact-report__total-amount">
            {{ report.packageTotalValue | flexyooNumber }}
          </span>
        </div>
      </div>
    </section>
  }

  @if (step() === 'report') {
    <p class="financial-impact-report__disclaimer">
      {{ 'employee.financial-impact-report.disclaimer' | translate: { date: disclaimerDate() } }}
    </p>
  }

  <div class="financial-impact-report__footer">
    <div class="financial-impact-report__footer-group">
      <button class="fo-color-error" matButton="filled" type="button" (click)="onClose()">
        {{ 'general.close' | translate }}
      </button>

      @if (showCartSimulationStep && step() === 'report') {
        <button matButton="filled" type="button" (click)="onBack()">
          {{ 'general.back' | translate }}
        </button>
      }
    </div>

    @if (step() === 'cart') {
      <button
        matButton="outlined"
        type="button"
        [disabled]="isLoading() || isCartEmpty() || hasError()"
        (click)="onOpenReport()"
      >
        {{ 'employee.financial-impact-report.open' | translate }}
      </button>
    } @else {
      <button
        matButton="outlined"
        type="button"
        [disabled]="isDownloading() || isLoading() || isCartEmpty() || hasError()"
        (click)="onDownloadPdf()"
      >
        <mat-icon>download</mat-icon>
        {{ 'employee.financial-impact-report.download-pdf' | translate }}
      </button>
    }
  </div>
</div>

<ng-template #benefitsTable let-benefits="benefits">
  <div class="financial-impact-report__table-scroll">
    <table class="financial-impact-report__table">
      <thead>
        <tr>
          <th>{{ 'employee.financial-impact-report.benefit-category' | translate }}</th>
          <th>{{ 'employee.financial-impact-report.benefit-choice' | translate }}</th>
          <th class="financial-impact-report__amount">
            {{ 'employee.financial-impact-report.funding-type' | translate }}
          </th>
        </tr>
      </thead>
      <tbody>
        @for (benefit of benefits; track benefit.benefitId) {
          <tr>
            <td>{{ benefit.category | flexyooLocalizedName: currLang() }}</td>
            <td>
              <span>{{ benefit.name | translate }}</span>
              @if (benefit.details.length || benefit.annualAmount) {
                <span class="financial-impact-report__benefit-details">
                  @for (detail of benefit.details; track detail.key) {
                    {{
                      'employee.financial-impact-report.details.' + detail.key
                        | translate: detail.params
                    }}
                    @if (!$last || benefit.annualAmount) {
                      ·
                    }
                  }
                  @if (benefit.annualAmount) {
                    {{
                      'employee.financial-impact-report.details.annual'
                        | translate: { amount: (benefit.annualAmount | flexyooNumber) }
                    }}
                  }
                </span>
              }
            </td>
            <td class="financial-impact-report__amount">
              @for (funding of benefit.fundings; track funding.family) {
                <span class="financial-impact-report__funding">
                  <span class="financial-impact-report__funding-amount">
                    {{ funding.amount | flexyooNumber }}
                  </span>
                  <span class="financial-impact-report__funding-family">
                    {{ 'employee.financial-impact-report.family.' + funding.family | translate }}
                  </span>
                </span>
              } @empty {
                <span>—</span>
              }
            </td>
          </tr>
        }
      </tbody>
    </table>
  </div>
</ng-template>
